import React, { Component, type ErrorInfo, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { WifiOff, RefreshCw } from "lucide-react";

// ─── Types ────────────────────────────────────────────────────────────────────

interface Props {
  children: ReactNode;
  /**
   * Optional fallback shown instead of the default UI.
   * Receives `retry` callback so the parent can wire its own button.
   */
  fallback?: (retry: () => void) => ReactNode;
}

interface State {
  hasError: boolean;
  isNetworkError: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** True for errors that look like a network / fetch failure */
function isNetworkLike(error: Error): boolean {
  const msg = error.message?.toLowerCase() ?? "";
  return (
    msg.includes("fetch") ||
    msg.includes("network") ||
    msg.includes("failed to load") ||
    msg.includes("load failed") ||
    msg.includes("networkerror") ||
    !navigator.onLine
  );
}

/** Fire-and-forget: log to platform_events. Never throws. */
function logBookingFailed(error: Error): void {
  // platform_events table not yet provisioned — log to console as a no-op.
  try {
    console.warn("[booking_failed]", {
      message: error.message,
      stack: error.stack?.split("\n").slice(0, 5).join("\n"),
      url: typeof window !== "undefined" ? window.location.href : null,
      online: typeof navigator !== "undefined" ? navigator.onLine : null,
      timestamp: new Date().toISOString(),
    });
  } catch {
    /* ignore */
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

export class NetworkErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, isNetworkError: false };
    this.retry = this.retry.bind(this);
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      isNetworkError: isNetworkLike(error),
    };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Log to platform_events — fire and forget, never blocks UI
    logBookingFailed(error);
    // Also surface to console for developers
    console.error("[NetworkErrorBoundary] caught:", error, info.componentStack);
  }

  retry(): void {
    this.setState({ hasError: false, isNetworkError: false });
  }

  render(): ReactNode {
    const { hasError, isNetworkError } = this.state;
    const { children, fallback } = this.props;

    if (!hasError) return children;

    if (fallback) return fallback(this.retry);

    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="max-w-sm w-full text-center space-y-6 p-6 border border-border rounded-2xl bg-card card-shadow">
          <div className="w-16 h-16 mx-auto rounded-full bg-muted flex items-center justify-center">
            <WifiOff className="w-8 h-8 text-muted-foreground" aria-hidden="true" />
          </div>

          <div className="space-y-2">
            <h2 className="text-xl font-heading font-semibold text-foreground">
              {isNetworkError ? "No connection" : "Something went wrong"}
            </h2>
            <p className="text-sm font-body text-muted-foreground">
              {isNetworkError
                ? "Please check your internet and try again."
                : "We hit an unexpected problem. Please try again."}
            </p>
          </div>

          <button
            type="button"
            onClick={this.retry}
            className="inline-flex items-center justify-center gap-2 w-full px-6 py-3 min-h-[44px] rounded-xl gradient-bg text-white text-sm font-body font-semibold hover:opacity-90 transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          >
            <RefreshCw className="w-4 h-4" aria-hidden="true" />
            Try again
          </button>

          <p className="text-xs font-body text-muted-foreground">
            If this keeps happening, contact us at{" "}
            <a
              href="mailto:hello@flexaura.me"
              className="underline hover:text-foreground transition-colors"
            >
              hello@flexaura.me
            </a>
          </p>
        </div>
      </div>
    );
  }
}

export default NetworkErrorBoundary;
